import { EtvConfig, PlayerConfig } from '@/interfaces/player-config.interface';
import { logService } from './log.service';

export class SchemaService {
  public setSchema(etvConfig: EtvConfig, playerConfig: PlayerConfig) {
    const schemaDataElement: any = document.getElementById('schemaData');

    if (!schemaDataElement) {
      logService.warn('Schema data element not found');
      return;
    }

    const schemaData = playerConfig.live ? this.getLiveSchema(etvConfig, playerConfig) : this.getPlaylistSchema(etvConfig, playerConfig);

    // Use textContent (standard) instead of .text (legacy)
    schemaDataElement.textContent = JSON.stringify(schemaData);
  }

  private getLiveSchema(etvConfig: EtvConfig, playerConfig: PlayerConfig) {
    // Check if stream is currently live (online)
    const isLive = etvConfig.stream?.status === 'online';

    // Determine name and description first (based on location or source)
    let schemaName: string;
    let schemaDescription: string;
    let contentLocation: any = undefined;

    if (!!playerConfig.location) {
      const { city, country, coordinates } = playerConfig.location;

      contentLocation = {
        '@type': 'AdministrativeArea',
        name: `${city[playerConfig.language]} / ${country[playerConfig.language]}`,
        geo: {
          '@type': 'GeoCoordinates',
          longitude: coordinates.lng,
          latitude: coordinates.lat,
        },
      };

      schemaName = `Live Cam: ${etvConfig.source?.title?.en || 'earthTV'} in ${city.en}, ${country.en} | earthTV Livestream`;
      schemaDescription = `Watch live footage of ${etvConfig.source?.title?.en || 'earthTV'} located in ${city.en}, ${country.en}. This earthTV webcam streams live 24/7. Tune in to check the current weather conditions and enjoy stunning views without leaving home.`;
    } else {
      const sourceTitle = etvConfig.source?.title?.en || 'earthTV Program';
      schemaName = `${sourceTitle} | earthTV Program`;
      schemaDescription = `Explore our planet with '${sourceTitle}', an earthTV program bringing you live and stunning views from across the globe. Each episode features a mix of dynamic cities and serene landscapes, enhanced with real-time weather updates and cultural event information. Experience the world in high-definition and discover its diverse beauty with our 24/7 live streams in multiple languages.`;
    }

    // Build schema with name and description FIRST (for CMS compatibility)
    const schemaData: any = {
      '@context': 'https://schema.org',
      '@type': 'VideoObject',
      name: schemaName,
      description: schemaDescription,
      embedUrl: `${window.location.origin}${window.location.pathname}`,
      contentUrl: playerConfig.parentFullUrl || window.location.href,
      thumbnailUrl: etvConfig.stream?.mediaUrls?.previewImage?.cdn || playerConfig?.previewImage,
      uploadDate: etvConfig.stream.updateTime,
      // For live streams: omit duration (24/7 continuous stream)
      // duration property not needed for BroadcastEvent type
      genre: 'travel, live nature, documentary, educational, weather, live, webcams',
      inLanguage: playerConfig.language || 'en',
      publisher: {
        '@type': 'Organization',
        name: 'earthTV',
        url: 'https://www.earthtv.com',
      },
    };

    // Add contentLocation if available
    if (contentLocation) {
      schemaData.contentLocation = contentLocation;
    }

    // Add broadcast event for live streams
    // Use BroadcastEvent for active livestreams (Google requirement for "LIVE" badge)
    if (playerConfig.live && isLive) {
      schemaData.publication = [
        {
          '@type': 'BroadcastEvent',
          isLiveBroadcast: true,
          startDate: etvConfig.stream.createTime || etvConfig.stream.updateTime,
          // For 24/7 continuous streams: omit endDate and duration
          // This indicates an ongoing live broadcast
        },
      ];
    } else if (playerConfig.live && !isLive) {
      // Stream exists but is offline - add publication with start and end date
      schemaData.publication = [
        {
          '@type': 'BroadcastEvent',
          isLiveBroadcast: false,
          startDate: etvConfig.stream.createTime || etvConfig.stream.updateTime,
          endDate: etvConfig.stream.updateTime, // When stream went offline
        },
      ];
    }

    return schemaData;
  }

  private getPlaylistSchema(etvConfig: EtvConfig, playerConfig: PlayerConfig) {
    // Calculate duration in ISO 8601 format (PT<seconds>S)
    const durationSeconds = etvConfig.clipMaxDuration || 300; // Default 5 minutes
    const duration = `PT${durationSeconds}S`;

    // Check if playlist has clips and first clip has a location
    // This happens when query filters by source (e.g., source.alias = BER)
    // In this case, all clips are from the same location → use location-based schema
    const firstClipLocation = playerConfig.playlist?.[0]?.location;

    // Determine name and description first (based on location if available)
    let schemaName: string;
    let schemaDescription: string;
    let contentLocation: any = undefined;

    if (firstClipLocation) {
      const { city, country, coordinates } = firstClipLocation;
      const sourceTitle = etvConfig.playlist?.name?.en || 'earthTV Playlist';

      contentLocation = {
        '@type': 'AdministrativeArea',
        name: `${city[playerConfig.language]} / ${country[playerConfig.language]}`,
        geo: {
          '@type': 'GeoCoordinates',
          longitude: coordinates.lng,
          latitude: coordinates.lat,
        },
      };

      schemaName = `${sourceTitle} in ${city.en}, ${country.en} | earthTV Clips`;
      schemaDescription = `Watch stunning clips from ${sourceTitle} located in ${city.en}, ${country.en}. This earthTV playlist features highlights and time-lapse footage from this location. Tune in to see beautiful views and discover the charm of ${city.en}.`;
    } else {
      // Generic playlist description when no specific location (mixed sources)
      const playlistName = etvConfig.playlist?.name?.en || 'earthTV Program';
      schemaName = `${playlistName} | earthTV Program`;
      schemaDescription = `Explore our planet with '${playlistName}', an earthTV program bringing you stunning views from across the globe. Experience the world in high-definition with our 24/7 streams.`;
    }

    // Build schema with name and description FIRST (for CMS compatibility)
    const schemaData: any = {
      '@context': 'https://schema.org',
      '@type': 'VideoObject',
      name: schemaName,
      description: schemaDescription,
      embedUrl: `${window.location.origin}${window.location.pathname}`,
      contentUrl: playerConfig.parentFullUrl || window.location.href,
      thumbnailUrl: playerConfig?.previewImage,
      uploadDate: etvConfig.playlist.updateTime,
      duration: duration,
      genre: 'travel, nature, documentary, educational',
      inLanguage: playerConfig.language || 'en',
      publisher: {
        '@type': 'Organization',
        name: 'earthTV',
        url: 'https://www.earthtv.com',
      },
    };

    // Add contentLocation if available
    if (contentLocation) {
      schemaData.contentLocation = contentLocation;
    }

    return schemaData;
  }
}

export const schemaService = new SchemaService();
